import type { RefObject } from "react";
import { useRef, useState, useEffect } from "react";
import { useScrollTop } from "@shared/utils.hooks";

const IO_DEBOUNCE_RATE = 100;
export function useCTAInView<T extends HTMLElement>(
  external?: RefObject<T>
): [RefObject<T>, boolean] {
  const internal = useRef<T | null>(null);
  const ref = (external as RefObject<T>) ?? internal;

  const [inView, _setInView] = useState<boolean | -1>(-1); // -1 = unknown
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setInView = (value: boolean) => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    _setInView(value);
  };

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    const initiallyInView = rect.top < window.innerHeight && rect.bottom > 0;
    _setInView(initiallyInView);

    // const enterObs = new IntersectionObserver(
    //   ([entry]) => setInView(entry.isIntersecting),
    //   { rootMargin: "0px 0px 0px 0px", threshold: 1 }
    // );

    const leaveObs = new IntersectionObserver(
      ([entry]) => {
        if (!leaveTimer.current) {
          leaveTimer.current = setTimeout(() => {
            setInView(entry.isIntersecting);
            leaveTimer.current = null;
          }, IO_DEBOUNCE_RATE);
        }
      },
      { rootMargin: "0px 0px 0px 0px", threshold: 1 }
    );

    // enterObs.observe(node);
    leaveObs.observe(node);

    return () => {
      // enterObs.disconnect();
      leaveObs.disconnect();
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };
  }, [ref]);

  return [ref, inView !== false];
}
export const useStickyProductInfo = () => {
  const [ref, ctaInView] = useCTAInView<HTMLDivElement>();
  const scrollTop = useScrollTop();

  const [useSticky, setUseSticky] = useState(false);
  useEffect(() => {
    if (ctaInView) setUseSticky(true);
  }, [ctaInView]);

  const showDescriptionAndAttributes = scrollTop <= 0;

  return {
    ref,
    useSticky,
    isScrolling: scrollTop > 0,
    showDescriptionAndAttributes
  };
};
